import { instance } from "api/apiBase";
import {
  CombinedIncomeVerificationSchema,
  FieldDisplaySetting,
  IncomeType,
  SupervisorDetails
} from "shared/types";
import {
  capitalize,
  getByPath,
  normalizeFile,
  normalizeFilename
} from "shared/utils";
import { CombinedIncomeSubStep } from "../../constants";
import {
  CombinedIncomeStepData,
  CreateIncomePayload,
  FlowStep,
  NavigationData
} from "./types";
import { employmentDetailsViews } from "./constants";

export const resolveFlowTarget = (
  edge: FlowStep["next"] | FlowStep["back"],
  data?: NavigationData
): CombinedIncomeSubStep | null => {
  if (!edge) return null;

  if (typeof edge === "string") return edge;

  for (const condition of edge)
    if (condition.condition(data)) return condition.target;

  return null;
};

export const uploadFilesToS3 = async (files: File[]) => {
  return Promise.all(
    files.map(async (file) => {
      const queryParams = new URLSearchParams({
        filename: normalizeFilename(file.name),
        type: file.type
      }).toString();

      const { data: presignData } = await instance.get<{ url: string }>(
        `/portal/v1/boom_screen/assets/presign?${queryParams}`
      );

      const s3Response = await fetch(presignData.url, {
        method: "PUT",
        body: normalizeFile(file),
        mode: "cors"
      });

      const uploadedUrl = s3Response.url.split("?")[0];

      return uploadedUrl;
    })
  );
};

export const shouldSkipSupervisorDetails = (
  verificationSchema: CombinedIncomeVerificationSchema | undefined,
  incomeType: IncomeType | null | undefined
): boolean => {
  const supervisorFields = getByPath(verificationSchema, [
    incomeType || IncomeType.Employment,
    "fields",
    "supervisor"
  ]) as SupervisorDetails | undefined;

  const fieldSettings = Object.values(supervisorFields || {});
  if (!fieldSettings.length) return false;

  return fieldSettings.every(
    (setting) => setting === FieldDisplaySetting.Hidden
  );
};

export const buildCreateIncomePayload = (
  stepData: CombinedIncomeStepData,
  { amount, pay_period }: { amount?: string; pay_period?: string }
): CreateIncomePayload => {
  const payload = {
    type: stepData.currentIncomeType as IncomeType,
    start_date: stepData.start_date,
    pay_period,
    amount: {
      cents: Number(amount) || 0,
      currency: "USD"
    },
    additional_data: {}
  } as CreateIncomePayload & { additional_data: Record<string, string> };

  if (stepData.company_name)
    payload.additional_data.company_name = stepData.company_name;
  if (stepData.job_title)
    payload.additional_data.job_title = stepData.job_title;
  if (stepData.supervisor)
    payload.additional_data.supervisor = {
      first_name: stepData.supervisor.first_name,
      last_name: stepData.supervisor.last_name,
      email: stepData.supervisor.contact_email,
      phone: stepData.supervisor.contact_phone
    };
  if (stepData.description)
    payload.additional_data.description = stepData.description;
  if (stepData.paid_from)
    payload.additional_data.paid_from = stepData.paid_from;

  return payload;
};

export const getCombinedIncomeSubStepTitle = (
  stepData: CombinedIncomeStepData
) => {
  if (
    !stepData.lastStep ||
    stepData.lastStep === CombinedIncomeSubStep.SelectIncomeType
  )
    return "Choose income type";

  if (
    employmentDetailsViews.includes(stepData.lastStep as CombinedIncomeSubStep)
  ) {
    const prefix =
      !stepData.currentIncomeType ||
      stepData.currentIncomeType === IncomeType.OtherIncome
        ? "Income"
        : capitalize(stepData.currentIncomeType).replaceAll("_", "-");

    return `${prefix} details`;
  }

  if (stepData.lastStep === CombinedIncomeSubStep.DocumentUpload)
    return "Upload documents";
};

export const getNewIncomeFlowStepCount = (subStep: CombinedIncomeSubStep) => {
  if (employmentDetailsViews.includes(subStep)) return 1;
  if (subStep === CombinedIncomeSubStep.DocumentUpload) return 2;

  return 0;
};
